import { tinyDeskShowcaseClips } from "@marketing/tiny-desk/config/tiny-desk-showcase";
import { transmission5DanceShowcaseClips } from "@marketing/transmission-5-dance/config/transmission-5-dance-showcase";
import { byeByeTrendShowcaseClips } from "@marketing/bye-bye-trend/config/bye-bye-trend-showcase";
import { hopHopHopDanceShowcaseClips } from "@marketing/hop-hop-hop-dance/config/hop-hop-hop-dance-showcase";
import { aiFrenchKissShowcaseClips } from "@marketing/ai-french-kiss/config/ai-french-kiss-showcase";
import { flyingCarShowcaseClips } from "@marketing/flying-car/config/flying-car-showcase";
import { teslaRoadsterShowcaseClips } from "@marketing/tesla-roadster/config/tesla-roadster-showcase";
import { imageEffectSamples } from "./image-effect-samples";
import { aiKissShowcaseClips } from "@marketing/ai-kiss/config/ai-kiss-showcase";
import { aiAirKissShowcaseClips } from "@marketing/ai-air-kiss/config/ai-air-kiss-showcase";
import { aiTongueKissShowcaseClips } from "@marketing/ai-tongue-kiss/config/ai-tongue-kiss-showcase";
import { aiDanceVideoShowcaseClips } from "@marketing/ai-dance-video/config/ai-dance-video-showcase";
import { aiHugShowcaseClips } from "@marketing/ai-hug/config/ai-hug-showcase";
import { aiWeddingShowcaseClips } from "@marketing/ai-wedding/config/ai-wedding-showcase";
import { bulin47ShowcaseClips } from "@marketing/bulin-47/config/bulin-47-showcase";
import { burningBridgesShowcaseClips } from "@marketing/burning-bridges/config/burning-bridges-showcase";
import { chuttamalleShowcaseClips } from "@marketing/chuttamalle/config/chuttamalle-showcase";
import { hotelLobbyShowcaseClips } from "@marketing/hotel-lobby/config/hotel-lobby-showcase";
import { ozilGoalShowcaseClips } from "@marketing/ozil-goal/config/ozil-goal-showcase";
import { rumpelstiltskinShowcaseClips } from "@marketing/rumpelstiltskin/config/rumpelstiltskin-showcase";
import { zombieTrendShowcaseClips } from "@marketing/zombie-trend/config/zombie-trend-showcase";
import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import { AI_TOOL_GROUPS } from "@shared/config/ai-tools";

/**
 * Studio 里的特效清单：/ai-effects 瀑布流、特效页的换特效弹窗和右栏 Templates tab 共用（2026-10-23）。
 * 归类规则老公 10/23 定：上传照片、出固定效果的算特效；换装、姿势、抠图、去水印、装修归 /ai-tools。
 */
export type EffectKind = "video" | "image";

export interface EffectCatalogItem {
	href: string;
	kind: EffectKind;
	/** 卡片标题的 i18n key（完整 key，不带命名空间前缀） */
	titleKey: string;
	poster: string;
	/** 卡头 48px 方图和 4:3 卡片用；竖版 poster 中间是黑天时单独给一张方图 */
	icon?: string;
	videoUrl?: string;
	/** 瀑布流按它定卡片比例；图片特效统一 3:4 */
	orientation: "landscape" | "portrait";
	badge?: "hot" | "new";
}

/** 视频热梗取各页样片区第一条的 poster 和横竖（同 video-trend-links.ts 的封面） */
function videoEffect(
	href: string,
	titleKey: string,
	clip:
		| {
				poster: string;
				icon?: string;
				videoUrl: string;
				orientation: "landscape" | "portrait";
		  }
		| undefined,
	badge?: EffectCatalogItem["badge"],
): EffectCatalogItem[] {
	return clip
		? [
				{
					href,
					kind: "video",
					titleKey,
					poster: clip.poster,
					icon: clip.icon,
					videoUrl: clip.videoUrl,
					orientation: clip.orientation,
					badge,
				},
			]
		: [];
}

/**
 * 顺序：视频按 9/22–10/22 英文浏览量排（Hotel Lobby 762、Zombie 634 挂 HOT），图片特效接在后面。
 * Bulin 47 是最新上线的热梗（10/15），挂 NEW
 */
export const EFFECTS_CATALOG: EffectCatalogItem[] = [
	...videoEffect(
		"/hotel-lobby-ai-video-generator",
		"hotelLobby.title",
		hotelLobbyShowcaseClips[0],
		"hot",
	),
	...videoEffect(
		"/zombie-trend-ai-video-generator",
		"zombieTrend.title",
		zombieTrendShowcaseClips[0],
		"hot",
	),
	...videoEffect(
		"/ai-kiss-video-generator",
		"aiKiss.title",
		aiKissShowcaseClips[0],
	),
	...videoEffect(
		"/rumpelstiltskin-ai-video-generator",
		"rumpelstiltskin.title",
		rumpelstiltskinShowcaseClips[0],
	),
	...videoEffect(
		"/burning-bridges-ai-video-generator",
		"burningBridges.title",
		burningBridgesShowcaseClips[0],
	),
	...videoEffect(
		"/chuttamalle-ai-video-generator",
		"chuttamalle.title",
		chuttamalleShowcaseClips[0],
	),
	...videoEffect(
		"/bulin-47-ai-video-generator",
		"bulin47.title",
		bulin47ShowcaseClips[0],
		"new",
	),
	// 2026-10-24 上线的热梗，挂 NEW
	...videoEffect(
		"/ozil-goal-ai-video-generator",
		"ozilGoal.title",
		ozilGoalShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/tesla-roadster-ai-video-generator",
		"teslaRoadster.title",
		teslaRoadsterShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/flying-car-ai",
		"flyingCar.title",
		flyingCarShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/ai-french-kiss-video-generator",
		"aiFrenchKiss.title",
		aiFrenchKissShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/tiny-desk-ai-video-generator",
		"tinyDesk.title",
		tinyDeskShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/transmission-5-dance-ai-video-generator",
		"transmission5Dance.title",
		transmission5DanceShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/bye-bye-trend-ai-video-generator",
		"byeByeTrend.title",
		byeByeTrendShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/hop-hop-hop-dance-ai-video-generator",
		"hopHopHopDance.title",
		hopHopHopDanceShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/ai-wedding-video-generator",
		"aiWedding.title",
		aiWeddingShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/ai-hug-video-generator",
		"aiHug.title",
		aiHugShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/ai-air-kiss-video-generator",
		"aiAirKiss.title",
		aiAirKissShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/ai-tongue-kiss-generator",
		"aiTongueKiss.title",
		aiTongueKissShowcaseClips[0],
		"new",
	),
	...videoEffect(
		"/ai-dance-video-generator",
		"aiDanceVideo.title",
		aiDanceVideoShowcaseClips[0],
		"new",
	),
	{
		href: "/photo-to-pixar",
		kind: "image",
		titleKey: "imageToPixar.title",
		poster: imageEffectSamples.pixar[1].after,
		orientation: "portrait",
	},
	{
		href: "/photo-to-anime",
		kind: "image",
		titleKey: "imageToAnime.title",
		poster: imageEffectSamples.anime[2].after,
		orientation: "portrait",
	},
	{
		href: "/photo-to-cartoon",
		kind: "image",
		titleKey: "imageToCartoon.title",
		poster: imageEffectSamples.cartoon[2].after,
		orientation: "portrait",
	},
	{
		href: "/age-filter",
		kind: "image",
		titleKey: "ageFilter.title",
		poster: buildR2AssetsImageUrl(
			"age-filter/banana-2-1-2026-10-26/entertainment-after.webp?v=960242b467",
		),
		orientation: "portrait",
	},
	{
		href: "/templates/ai-smile-generator",
		kind: "image",
		titleKey: "aiImageGenerator.templates.ai-smile-generator.hero.title",
		poster: imageEffectSamples.smile[0].after,
		orientation: "portrait",
	},
	{
		href: "/ai-buzz-cut",
		kind: "image",
		titleKey: "aiBuzzCut.title",
		poster: imageEffectSamples.buzz[0].after,
		orientation: "portrait",
	},
	{
		href: "/templates/image-to-emoji-converter",
		kind: "image",
		titleKey:
			"aiImageGenerator.templates.image-to-emoji-converter.hero.title",
		poster: "/images/image-to-emoji/nano-banana-2.jpg",
		orientation: "portrait",
	},
	{
		href: "/ai-muscle-generator",
		kind: "image",
		titleKey: "aiImageGenerator.templates.ai-muscle-generator.hero.title",
		poster: buildR2AssetsImageUrl(
			"home/showcase/ai-muscle/results/man-tee-bodybuilder-1k.webp",
		),
		orientation: "portrait",
		badge: "new",
	},
	{
		href: "/ai-big-butt",
		kind: "image",
		titleKey: "aiImageGenerator.templates.ai-big-butt.hero.title",
		poster: buildR2AssetsImageUrl(
			"home/showcase/ai-big-butt/results/woman-dress-curvy-v1-1k.webp",
		),
		orientation: "portrait",
		badge: "new",
	},
	{
		href: "/ai-feet-generator",
		kind: "image",
		titleKey: "aiImageGenerator.templates.ai-feet-generator.hero.title",
		poster: buildR2AssetsImageUrl(
			"home/showcase/ai-feet/samples/woman-sofa-chair-1k.webp",
		),
		orientation: "portrait",
		badge: "new",
	},
];

/**
 * 工作台右栏的 Templates tab（10/23 老公定，照 SuperMaker 的 AI Video Maker），和 History 一样常驻：
 * 视频工作台（含特效页）只放视频特效，生图工作台放图片特效 + 改图工具（/ai-tools 上按 kind 分）
 */
export const WORKBENCH_TEMPLATES: Record<EffectKind, EffectCatalogItem[]> = {
	video: workbenchTemplates("video"),
	image: workbenchTemplates("image"),
};

function workbenchTemplates(kind: EffectKind): EffectCatalogItem[] {
	return [
		...EFFECTS_CATALOG.filter((item) => item.kind === kind),
		...AI_TOOL_GROUPS.flatMap((group) => group.tools)
			.filter((tool) => tool.kind === kind)
			.map(
				(tool): EffectCatalogItem => ({
					href: tool.href,
					kind,
					titleKey: tool.titleKey,
					poster: tool.afterImage ?? tool.beforeImage,
					orientation: "portrait",
				}),
			),
	];
}
